import React, { useState } from 'react';
import { 
  Modal, 
  Card, 
  Button, 
  Typography, 
  Row, 
  Col, 
  Tag, 
  Space, 
  Divider,
  Alert,
  Spin,
  message
} from 'antd';
import {
  CrownOutlined,
  RocketOutlined,
  StarOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  ArrowUpOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { PLANS, PLAN_FEATURES } from '../../services/planService';
import { subscriptionApi } from '../../utils/api';

const { Title, Text, Paragraph } = Typography;

/**
 * Modal de mise à niveau de plan
 */
const UpgradeModal = ({ 
  visible, 
  onCancel, 
  currentPlan, 
  onUpgrade 
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Obtenir l'icône du plan
   */
  const getPlanIcon = (plan) => {
    const icons = {
      [PLANS.DECOUVERTE]: <InfoCircleOutlined />,
      [PLANS.ESSENTIEL]: <RocketOutlined />,
      [PLANS.CROISSANCE]: <StarOutlined />,
      [PLANS.EXCELLENCE]: <CrownOutlined />
    };
    return icons[plan] || <InfoCircleOutlined />;
  };

  /**
   * Obtenir la couleur du plan
   */
  const getPlanColor = (plan) => {
    const colors = {
      [PLANS.DECOUVERTE]: 'default',
      [PLANS.ESSENTIEL]: 'blue',
      [PLANS.CROISSANCE]: 'cyan',
      [PLANS.EXCELLENCE]: 'purple'
    };
    return colors[plan] || 'default';
  };

  /**
   * Obtenir le prix du plan (simulé)
   */
  const getPlanPrice = (plan) => {
    const prices = {
      [PLANS.DECOUVERTE]: 0,
      [PLANS.ESSENTIEL]: 15000, // 15,000 XOF
      [PLANS.CROISSANCE]: 35000, // 35,000 XOF
      [PLANS.EXCELLENCE]: 75000  // 75,000 XOF
    };
    return prices[plan] || 0;
  };

  /**
   * Vérifier si un plan est supérieur au plan actuel
   */
  const isPlanUpgrade = (plan) => {
    const planOrder = [PLANS.DECOUVERTE, PLANS.ESSENTIEL, PLANS.CROISSANCE, PLANS.EXCELLENCE];
    return planOrder.indexOf(plan) > planOrder.indexOf(currentPlan);
  };

  /**
   * Gérer la sélection d'un plan
   */
  const handlePlanSelect = (plan) => {
    if (!isPlanUpgrade(plan)) return;
    setSelectedPlan(plan);
  };

  /**
   * Confirmer la mise à niveau
   */
  const handleUpgrade = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      // Appel API réel pour la mise à niveau
      const response = await subscriptionApi.upgradePlan(selectedPlan);
      
      if (response.data.success) {
        message.success(`Mise à niveau vers le plan ${PLAN_FEATURES[selectedPlan].name} réussie !`);
        
        // Notifier le composant parent avec les nouvelles données
        if (onUpgrade) {
          onUpgrade(selectedPlan, response.data.data);
        }
        
        onCancel();
        setSelectedPlan(null);
      } else {
        message.error(response.data.message || 'Erreur lors de la mise à niveau');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la mise à niveau du plan';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtenir les plans disponibles pour mise à niveau
   */
  const getAvailablePlans = () => {
    const allPlans = Object.keys(PLAN_FEATURES);
    return allPlans.filter(plan => plan !== currentPlan);
  };

  /**
   * Rendu d'une carte de plan
   */
  const renderPlanCard = (planKey) => {
    const planConfig = PLAN_FEATURES[planKey];
    const isUpgrade = isPlanUpgrade(planKey);
    const isCurrent = planKey === currentPlan;
    const isSelected = planKey === selectedPlan;
    const price = getPlanPrice(planKey);

    return (
      <Card
        key={planKey}
        hoverable={isUpgrade}
        onClick={() => handlePlanSelect(planKey)}
        className={`h-full relative ${
          isSelected ? 'border-blue-500 shadow-lg' : ''
        } ${!isUpgrade && !isCurrent ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={{ 
          borderColor: isSelected ? '#1890ff' : undefined,
          background: isCurrent ? '#f0f8ff' : undefined
        }}
      >
        {/* Badge plan actuel */}
        {isCurrent && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <Tag color="green">Plan actuel</Tag>
          </div>
        )}

        {/* Badge recommandé pour le plan suivant */}
        {isUpgrade && isPlanUpgrade(planKey) && (
          <div className="absolute -top-3 right-4">
            <Tag color="orange">Recommandé</Tag>
          </div>
        )}

        <div className="text-center">
          {/* Icône et nom du plan */}
          <div className="text-3xl mb-2 text-center">
            {getPlanIcon(planKey)}
          </div>
          
          <Title level={4} className="mb-2">
            {planConfig.name}
          </Title>

          {/* Prix */}
          <div className="mb-4">
            <Title level={2} className="mb-0" style={{ color: getPlanColor(planKey) === 'purple' ? '#722ed1' : '#1890ff' }}>
              {price === 0 ? 'Gratuit' : `${price.toLocaleString()} XOF`}
            </Title>
            <Text type="secondary">par mois</Text>
          </div>

          <Divider />

          {/* Limites principales */}
          <div className="space-y-2 text-left mb-4">
            <div className="flex justify-between">
              <Text>Messages/mois</Text>
              <Text strong>
                {planConfig.maxMessagesPerMonth === -1 ? 'Illimité' : planConfig.maxMessagesPerMonth}
              </Text>
            </div>
            <div className="flex justify-between">
              <Text>Produits</Text>
              <Text strong>
                {planConfig.maxProducts === -1 ? 'Illimité' : planConfig.maxProducts}
              </Text>
            </div>
            <div className="flex justify-between">
              <Text>Boutiques</Text>
              <Text strong>
                {planConfig.maxShops === -1 ? 'Illimité' : planConfig.maxShops}
              </Text>
            </div>
          </div>

          {/* Fonctionnalités principales */}
          <div className="text-left mb-4">
            <Text strong>Fonctionnalités :</Text>
            <ul className="mt-2 text-sm">
              {planConfig.features.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-center space-x-2 mb-1">
                  <CheckOutlined className="text-green-500" />
                  <span>{feature.replace(/_/g, ' ')}</span>
                </li>
              ))}
              {planConfig.features.length > 3 && (
                <li className="text-gray-500">
                  + {planConfig.features.length - 3} autres fonctionnalités
                </li>
              )}
            </ul>
          </div>

          {/* Bouton de sélection */}
          {isCurrent ? (
            <Button disabled block>
              Plan actuel
            </Button>
          ) : !isUpgrade ? (
            <Button disabled block>
              Rétrogradation non disponible
            </Button>
          ) : (
            <Button 
              type={isSelected ? "primary" : "default"}
              block
              icon={isSelected ? <CheckOutlined /> : <ArrowUpOutlined />}
            >
              {isSelected ? 'Sélectionné' : 'Choisir ce plan'}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <ArrowUpOutlined />
          <span>Mettre à niveau mon plan</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="upgrade"
          type="primary"
          loading={loading}
          disabled={!selectedPlan}
          onClick={handleUpgrade}
          icon={<DollarOutlined />}
        >
          {loading ? 'Traitement du paiement...' : `Passer au plan ${selectedPlan ? PLAN_FEATURES[selectedPlan].name : ''}`}
        </Button>
      ]}
    >
      <div className="space-y-6">
        {/* Message d'information */}
        <Alert
          message="Choisissez votre nouveau plan"
          description="Le paiement sera simulé et votre plan sera mis à jour immédiatement en base de données."
          type="info"
          showIcon
        />

        {/* Grille des plans */}
        <Row gutter={[16, 16]}>
          {getAvailablePlans().map((planKey) => (
            <Col xs={24} sm={12} lg={6} key={planKey}>
              {renderPlanCard(planKey)}
            </Col>
          ))}
        </Row>

        {/* Informations sur le plan sélectionné */}
        {selectedPlan && (
          <Card className="bg-blue-50 border-blue-200">
            <div className="text-center">
              <Title level={4} className="mb-2">
                Récapitulatif de votre mise à niveau
              </Title>
              
              <div className="flex justify-center items-center space-x-4 mb-4">
                <div className="text-center">
                  <Tag color={getPlanColor(currentPlan)}>
                    {PLAN_FEATURES[currentPlan].name}
                  </Tag>
                  <div className="text-sm text-gray-500">Plan actuel</div>
                </div>
                
                <ArrowUpOutlined className="text-xl text-blue-500" />
                
                <div className="text-center">
                  <Tag color={getPlanColor(selectedPlan)}>
                    {PLAN_FEATURES[selectedPlan].name}
                  </Tag>
                  <div className="text-sm text-gray-500">Nouveau plan</div>
                </div>
              </div>

              <div className="bg-white p-4 rounded border">
                <div className="flex justify-between items-center">
                  <Text>Coût mensuel :</Text>
                  <Text strong className="text-lg">
                    {getPlanPrice(selectedPlan).toLocaleString()} XOF/mois
                  </Text>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Facturation mensuelle - Paiement simulé pour les tests
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};

export default UpgradeModal; 